// Shared StakeTek product data.
// Keep claims practical and evidence-based: no invented rankings, reviews, or pickup addresses.

export const FINISHES = ["Gal Silver", "Black"];

export const BRACKET = {
  id: "bracket",
  name: "StakeTek Supported Slide-On Bracket",
  shortName: "Star Picket Brackets",
  // Customer-facing range name — distinguishes it from the Flat Slide-On
  // Bracket. Both use the same slide-over mounting system; this one has the
  // lower timber support lip.
  rangeName: "Supported Slide-On Bracket",
  finishes: FINISHES,
  blurb:
    "Our bracket is built around a 2.0 mm galvanised steel sleeve with a 2.5 mm Duragal support angle, and slides straight over standard Y-profile star pickets. Designed and manufactured in Victoria, it gives you an adjustable, removable and reusable mounting point for timber, fencing and outdoor projects — attach timber to star pickets without welding or fabricating brackets on site.",
  blurb2:
    "The standard bracket is designed around 45 × 90 mm F5 timber and includes an approximately 40 mm support lip to help hold the timber properly. Slide the bracket over the picket, set it at the height you need, then bolt or screw your material into place.",
  compatibility:
    "Designed for standard Y-profile star pickets. Custom, oversized and non-standard agricultural profiles may vary. Not compatible with StakeTek tube-type pickets. Contact us with a photo or measurements if you are unsure.",
  angleSizes:
    "The bracket uses a 2.5 mm Duragal support angle on a 2.0 mm galvanised steel sleeve — designed for the vast majority of fencing and landscaping applications. The 2.5 mm measurement refers to the support-angle component only.",
  features: [
    "2.5 mm Duragal support angle",
    "2.0 mm galvanised steel sleeve",
    "Designed and manufactured in Victoria",
    "Slides over standard Y-profile star pickets",
    "Designed around 45 × 90 mm F5 timber",
    "Approx. 40 mm timber support lip",
    "No on-site welding or custom fabrication required",
    "Adjustable, removable and reusable",
    "Designed for long-term outdoor use",
    "Bulk packs available",
  ],
  applications: [
    "Rural fencing",
    "Garden fencing",
    "Temporary fencing",
    "Temporary structures and site setups",
    "Wind protection",
    "Privacy screens",
    "Construction sites",
    "Boundary fencing",
    "Animal enclosures",
    "Landscaping jobs",
    "DIY property repairs",
  ],
  faqs: [
    {
      q: "Will StakeTek brackets fit every star picket?",
      a: "StakeTek slide-on brackets are designed for standard Y-profile star pickets commonly used in Australia. Some custom, oversized or agricultural picket profiles may differ and may not fit. StakeTek tube-type pickets are not compatible with the current slide-on bracket. If you are unsure, contact us with a clear photo or measurements before ordering.",
    },
    {
      q: "What is the bracket made from?",
      a: "The bracket uses a 2.5 mm Duragal support angle on a 2.0 mm galvanised steel sleeve, designed for the vast majority of fencing and landscaping applications. The 2.5 mm measurement refers to the support-angle component only.",
    },
    {
      q: "Can I use them with timber?",
      a: "Yes. They are commonly used to fix timber rails, screens, edging and garden structures to star pickets.",
    },
    {
      q: "Can I use them with steel?",
      a: "Yes. Use suitable bolts or screw in fixings for the steel section you are fixing.",
    },
    {
      q: "Can I use them with Colorbond?",
      a: "Yes. They can be used as a fixing point for Colorbond and sheet materials when installed with appropriate fasteners.",
    },
    {
      q: "Do I need to drill or weld the star picket?",
      a: "No on-site welding or custom fabrication is required. Simply slide the bracket onto the star picket, adjust the height and secure your timber. You still drill, screw or bolt into the material you are fixing.",
    },
    {
      q: "Are they galvanised?",
      a: "Yes. The brackets are made from heavy-duty galvanised steel for corrosion resistance in Australian outdoor conditions.",
    },
    {
      q: "How many brackets do I need?",
      a: "Most jobs use at least one bracket per fixing point. Small repairs often start with a 10 pack, small fence sections suit a 20 pack, and boundary or contractor jobs usually move into the 30, 40 and 50 packs — the 50 pack is the best value per bracket.",
    },
    {
      q: "What timber size are the standard brackets designed for?",
      a: "The standard StakeTek bracket is designed around 45 × 90 mm F5 timber and includes an approximately 40 mm support lip. A 25 × 150 mm plinth timber support option is also available for wider timber-support applications, and other angle options may be available for selected projects.",
    },
    {
      q: "What is included in a StakeTek Installation Kit?",
      a: "Kits are matched to the bracket type, timber support and pack size. The standard set includes two 40 mm outdoor timber screws, one M8 × 40 mm galvanised bolt and one M8 galvanised nut per bracket — so a 20-bracket kit includes 40 screws, 20 bolts and 20 nuts. A plinth kit with shorter screws suits the 25 × 150 mm timber support.",
    },
    {
      q: "What is the flat bracket used for?",
      a: "The StakeTek Flat Slide-On Bracket uses the same slide-over mounting system as the supported bracket, but with a flat mounting design instead of a lower timber support lip. It is a versatile option for timber, fencing, construction and temporary setups — particularly useful where a flatter fixing surface or easier removal is preferred.",
    },
    {
      q: "Are flat brackets dispatched immediately?",
      a: "Flat brackets are usually dispatched within 3–5 business days, the same as our main supported bracket.",
    },
    {
      q: "What warranty do the brackets have?",
      a: "They carry a lifetime warranty against manufacturing defects under normal use. The brackets are heavy-duty steel and hard to damage in normal use — if one ever fails from a manufacturing fault, we'll replace it. The warranty does not cover incorrect installation, misuse, modifications, impact damage, harsh chemical exposure, abnormal load use, or general wear from extreme conditions.",
    },
    {
      q: "Do you accept returns?",
      a: "Returns are accepted where required under Australian Consumer Law, including for faulty, damaged or incorrect items. Change-of-mind returns are not accepted unless approved by StakeTek before return. If there's an issue with your order, contact us with your order number and clear photos so we can assess it and arrange the appropriate remedy. Warranty and consumer guarantee rights are separate from change-of-mind returns.",
    },
    {
      q: "Where do you ship?",
      a: "We ship Australia-wide through online checkout, with freight calculated by weight and delivery address. Free local pickup is available in Victoria. New Zealand orders are available by custom freight quote — please contact us before ordering.",
    },
    {
      q: "How long does delivery take?",
      a: "Brackets are usually dispatched within 3-5 business days. Larger orders and non-standard timber supports can take longer to dispatch. Delivery times then vary by location, courier and Australia Post transit times. Tracking is provided when your order ships, and local pickup orders are emailed as soon as they're ready to collect.",
    },
    {
      // Keep these figures in sync with the Shopify shipping tiers (see lib/shipping.js).
      q: "How much does shipping cost?",
      a: "Shipping is calculated by weight at checkout, from $10.95 for a single bracket up to $125 for the largest parcel orders. As a guide, a 20 pack ships anywhere in Australia for $32.95. Very large orders are quoted freight separately. Local pickup in Victoria is free.",
    },
    {
      q: "Why does shipping cost more for larger orders?",
      a: "StakeTek brackets are made from heavy-duty galvanised steel, so larger orders can become heavy quickly. Shipping is calculated on packed weight, order size and delivery location. These are solid steel hardware, not lightweight plastic or thin sheet accessories, so freight reflects the real weight of the packed order. We keep it as practical as we can.",
    },
    {
      q: "Can I pick up locally?",
      a: "Yes, and it's free. Choose Local Pickup at checkout and collect from Resale Therapy on Main, Emerald VIC 3782. We email you as soon as your order is ready.",
    },
    {
      q: "Do you offer volume savings?",
      a: "Yes — volume savings apply automatically as you increase the quantity in the configurator. The 2.5 mm Duragal bracket is $7.50 each up to 19, then steps down to $7.25 each at 20, $7.20 at 30, $7.15 at 40 and $7.00 each at 50 and above. For custom orders or larger projects, get in touch.",
    },
    {
      q: "How is the supported bracket priced?",
      a: "The 2.5 mm Duragal Australian-made bracket is $7.50 per bracket at the single-unit price. Pack pricing lowers the per-bracket price as quantity increases: $145.00 for 20 ($7.25 each), $216.00 for 30 ($7.20 each), $286.00 for 40 ($7.15 each) and $350.00 for 50 ($7.00 each). $7.00 per bracket is our lowest published price.",
    },
    {
      q: "Do you offer trade pricing?",
      a: "Volume savings are already built into the online pricing, so most contractors and trade buyers just set the quantity that suits the job. For stockist supply, or an unusually large or custom order, get in touch.",
    },
    {
      q: "Do you ship to New Zealand?",
      a: "Yes. New Zealand orders are available by custom freight quote. Because the brackets are heavy-duty steel, NZ freight depends on order size and delivery postcode, so we quote it manually rather than through automatic checkout rates. Please contact us before ordering and we'll confirm the best freight price.",
    },
    {
      q: "Do you ship internationally?",
      a: "Yes. StakeTek accepts international enquiries from selected countries. Due to the weight and dimensions of steel products, international freight is quoted individually before payment. Delivery estimates are included with the quotation. Prices are shown in AUD, and the freight cost and estimated delivery time are confirmed before payment. Request an international freight quote and we'll come back with the product total, freight cost and estimated delivery timeframe.",
    },
    {
      q: "Are they removable and reusable?",
      a: "Yes. Back out the fixings, slide the bracket off the picket and reuse it on the next setup.",
    },
  ],
};

export const FLAT_BRACKET = {
  id: "flat-bracket",
  name: "StakeTek Flat Slide-On Bracket",
  shortName: "Flat Slide-On Bracket",
  rangeName: "Flat Slide-On Bracket",
  blurb:
    "The StakeTek Flat Slide-On Bracket uses the same slide-over mounting system as our supported bracket, but with a flat fixing surface and no lower support lip.",
  blurb2:
    "Designed for applications where a simple flat fixing surface is preferred. Suitable for timber, fencing, construction and temporary setups, and particularly useful where a lower support lip is not required.",
  features: [
    "Australian made",
    "Slides onto compatible Y-profile star pickets",
    "Flat fixing surface — no lower support lip",
    "Height adjustable using the picket holes",
    "Suits concrete formwork and boxing timber",
    "Removable and reusable",
    "Suitable where a lower support lip is not required",
    "Available in matched pack sizes",
  ],
  applications: [
    "Concrete formwork",
    "Slab, path and garden edge boxing",
    "Timber and fencing work",
    "Construction and site work",
    "Temporary timber support",
  ],
  faqs: [
    {
      q: "How is the flat bracket different from the supported bracket?",
      a: "Both brackets slide over standard Y-profile star pickets and give you a flat face to fix timber to. The supported bracket includes a lower support lip that helps hold the timber from underneath. The flat bracket has a flat fixing surface with no lower support lip, suited to applications where a simpler flat fixing point is what you need.",
    },
    {
      q: "What is the flat bracket used for?",
      a: "It suits applications requiring a simple flat fixing surface without the lower support lip — timber and fencing work, construction and site setups, concrete boxing, formwork and temporary timber support are all common uses.",
    },
    {
      q: "Will it fit my star pickets?",
      a: "The flat bracket is designed for the same standard Y-profile star pickets as the supported bracket. Custom, oversized or non-standard agricultural profiles may vary — contact us with a photo or measurements if you are unsure.",
    },
    {
      q: "Are flat brackets dispatched immediately?",
      a: "Flat brackets are usually dispatched within 3–5 business days, the same as our main supported bracket.",
    },
    {
      q: "How is the flat bracket priced?",
      a: "The flat bracket is $7.00 per bracket up to 19, then $6.75 each at 20, $6.70 at 30, $6.65 at 40 and $6.50 each from 50 — the same automatic volume structure as the supported bracket. One configuration, with a flat fixing surface.",
    },
    {
      q: "Can I use the flat bracket for concrete formwork?",
      a: "Yes, it is one of the most common uses. Drive a star picket outside the boxing line, slide the flat bracket over it, set the height using the picket holes and bolt it, then screw the boxing timber to the flat face. The flat face is what suits formwork: there is no lip under the timber, so the boxing sits flush.",
    },
    {
      q: "Does the bracket come off after the pour?",
      a: "Yes. Back the fixings out, lift the bracket off the picket and pull the picket. Both get used again on the next job.",
    },
    {
      q: "Why use star pickets instead of timber pegs for formwork?",
      a: "Pickets drive into hard ground where timber pegs split, and the bracket gives a square fixing face at whatever height the boxing needs. Nothing gets cut up, so the same pickets and brackets get used again on the next job.",
    },
    {
      q: "Can I add an installation kit?",
      a: "Yes. The flat bracket uses the same optional installation kit as the supported bracket — two 40 mm outdoor timber screws, one M8 × 40 mm galvanised bolt and one M8 galvanised nut per bracket, matched to your selected pack size.",
    },
  ],
};

export const INSTALLATION_KIT = {
  id: "installation-kits",
  name: "StakeTek Installation Kits",
  shortName: "Installation Kits",
  blurb:
    "Complete your setup with the matched StakeTek Installation Kit. Each kit includes the correct quantity of 40 mm outdoor timber screws, M8 × 40 mm galvanised bolts and M8 galvanised nuts for your selected bracket pack.",
  blurb2:
    "Fixing kits are matched to the selected bracket type, timber support and quantity, so there is no working out screw and bolt counts on the fly.",
  faqs: [
    {
      q: "What is included in a StakeTek Installation Kit?",
      a: "The Standard Installation Kit includes two 40 mm outdoor timber screws, one M8 × 40 mm galvanised bolt and one M8 galvanised nut per bracket. The Plinth Installation Kit uses shorter outdoor screws suited to 25 × 150 mm plinth timber with the same bolt and nut per bracket.",
    },
    {
      q: "What kit sizes are available?",
      a: "Kits are sized to match the bracket packs: 1, 10, 20, 30, 40 and 50 bracket kits.",
    },
    {
      q: "Which kit suits which bracket?",
      a: "Choose the Standard kit for 45 × 90 mm F5 timber brackets and for flat brackets — both use the same hardware. Choose the Plinth kit for 25 × 150 mm plinth timber brackets, which use shorter 20 mm screws. On each bracket page the matched kit is picked automatically.",
    },
    {
      q: "Are washers included?",
      a: "No. Each set is two outdoor screws, one M8 × 40 mm galvanised bolt and one M8 galvanised nut per bracket.",
    },
  ],
};

export const STAR_PICKET = {
  id: "star-picket",
  name: "StakeTek Galvanised Tube-Type Star Pickets",
  shortName: "Tube-Type Star Pickets",
  finishes: FINISHES,
  sizes: [
    { mm: 900 },
    { mm: 1350 },
    { mm: 1500 },
    { mm: 1650 },
    { mm: 1800 },
    { mm: 2100 },
  ],
  blurb:
    "Premium galvanised steel tube-type star pickets for fencing, property, rural and trade use. Available in multiple lengths, with Gal Silver or Black finish options.",
  blurb2:
    "A practical tube-style steel picket range for fence runs, temporary fencing, property support and general trade use.",
  features: [
    "Premium galvanised steel construction",
    "Tube-type steel picket profile",
    "Consistent hole spacing",
    "Available in Gal Silver or Black finish",
    "Optional pointer for hard ground",
    "Small batches through to repeat supply",
  ],
  applications: [
    "Fencing and boundary work",
    "Temporary fencing",
    "Rural and property use",
    "Garden and property support",
    "Site and general trade use",
  ],
  faqs: [
    {
      q: "What sizes are available?",
      a: "Six standard lengths are listed: 900mm, 1350mm, 1500mm, 1650mm, 1800mm and 2100mm. Custom production runs may be available on request.",
    },
    {
      q: "What finishes are available?",
      a: "Star pickets are supplied in Black or Gal Silver finish.",
    },
    {
      q: "Do you supply small orders and larger projects?",
      a: "Yes. StakeTek supplies small orders through to project quantities - send the details through the quote form.",
    },
    {
      q: "Do I need pointers?",
      a: "Pointers are optional and may help in hard, rocky or compacted ground.",
    },
    {
      q: "What are typical lead times?",
      a: "Lead times vary by quantity, finish and size. Send your project details for current availability.",
    },
  ],
};

export const ANIMAL_HURDLE = {
  id: "animal-hurdle",
  name: "StakeTek 27 deg Animal Extension Hurdle",
  shortName: "27 deg Animal Extension Hurdle",
  finishes: FINISHES,
  blurb:
    "A slot-in angled extension hurdle that raises fence height by up to 600mm for animal and wildlife exclusion applications.",
  blurb2:
    "The hurdle slots into compatible StakeTek tube pickets and can be configured for different exclusion needs across gardens, boundaries, crops and property works.",
  specs: [
    { label: "Angle", value: "27 deg outward extension" },
    { label: "Added Fence Height", value: "Up to 600mm" },
    { label: "Adjustment", value: "3 levels" },
    { label: "Mounting", value: "Slots into compatible StakeTek tube pickets" },
    { label: "Material", value: "2mm steel" },
    { label: "Finishes", value: "Black or Gal Silver" },
    { label: "Use Case", value: "Animal and wildlife exclusion" },
  ],
  features: [
    "Adds up to 600mm of effective fence height",
    "27 deg angled extension for exclusion geometry",
    "3-level adjustment",
    "Slot-in retrofit to compatible StakeTek tube pickets",
    "Available in Black or Gal Silver finish",
    "2mm steel construction",
  ],
  applications: [
    "Animal and wildlife exclusion fencing",
    "Crop, orchard and vineyard protection",
    "Property boundary upgrades",
    "Garden and landscape enclosures",
    "Civil and infrastructure site security",
    "Rural and agricultural retrofits",
  ],
  faqs: [
    {
      q: "What is the 27 deg hurdle designed for?",
      a: "It is an angled fence extension used to increase effective fence height for animal and wildlife exclusion.",
    },
    {
      q: "What does it fit?",
      a: "It slots into the holes along compatible StakeTek tube pickets — no bolting and no drilling. It does not fit ordinary Y-profile star pickets. Check compatibility before ordering.",
    },
    {
      q: "How does the 3-level adjustment work?",
      a: "The hurdle can be set at different positions to tune the exclusion height for the job.",
    },
    {
      q: "What finishes are available?",
      a: "Black or Gal Silver finish options are available.",
    },
  ],
};

export const PRODUCTS = [BRACKET, FLAT_BRACKET, INSTALLATION_KIT, STAR_PICKET, ANIMAL_HURDLE];

export const APPLICATIONS = [
  { title: "Rural and Agricultural Fencing", desc: "Practical hardware for fence repairs, property runs and rural infrastructure." },
  { title: "Contractors and Builders", desc: "Fast fixing options for temporary works, site boundaries and practical construction jobs." },
  { title: "Landscaping and Gardens", desc: "Useful for garden beds, screens, edging and property improvements." },
  { title: "DIY Property Repairs", desc: "Simple bracket hardware for owners who need a sturdy fixing point without on-site welding." },
  { title: "Temporary Structures", desc: "Removable and reusable fixings for seasonal setups and temporary fencing." },
  { title: "Animal and Boundary Work", desc: "Hardware options for boundary lines, enclosures and animal exclusion applications." },
];

export const INNOVATION_PRINCIPLES = [
  {
    title: "Practical Fixing Hardware",
    desc: "StakeTek products are designed around real property jobs: strong fixing points, simple installation and reusable hardware.",
  },
  {
    title: "Australian Family-Owned Supply",
    desc: "StakeTek is an Australian family-owned manufacturer and supplier focused on practical fencing products for local conditions.",
  },
  {
    title: "Built For The Job",
    desc: "The product range supports rural owners, contractors, landscapers, builders and DIY property repairs.",
  },
];
